import {
  toWSDate,
  WSTable,
  WSTableCell,
  WSTableColumn,
  WSTableProps
} from "@wingspanhq/fe-component-library";
import {
  IEngagement,
  IRequirementDefinitionResponse,
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { mapRequirementDefinitionTemplateTierConfig } from "../../config/maps";
import { useRequirementDefinitionDefaultActions } from "../../hooks/useRequirementDefinitionDefaultActions";
import {
  buildRequirementDetailsPath,
  useGoToRequirementDefinitionDetails
} from "../../paths/helpers";
import {
  getRequirementDefinitionTableCellProps,
  getRequirementDefinitionTier
} from "../../utils";
import { useRequirementDefinitionsSetting } from "../../../../query/requirementDefinitions/query/useRequirementDefinitionsSetting";
import { selectorPolicyTypesFromSettings } from "../../../Requirements/modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";

export interface RequirementsTableProps
  extends Omit<WSTableProps, "tableData"> {
  listPath: string;
  requirements: IRequirementDefinitionResponse[];
  engagements: IEngagement[];
}

export const TypeCell: React.FC<{
  requirementType: RequirementType;
  requirementDataSourceSettingsId?: string;
}> = ({ requirementType, requirementDataSourceSettingsId }) => {
  const querySettings = useRequirementDefinitionsSetting({
    requirementDefinition: {
      requirementType,
      dataSourceId: requirementDataSourceSettingsId!
    },
    fetch: "contextType"
  });

  const dataSourceSettings = querySettings.data;

  const props = getRequirementDefinitionTableCellProps({
    type: requirementType,
    contextType:
      dataSourceSettings && "contextType" in dataSourceSettings
        ? dataSourceSettings.contextType
        : undefined,
    policyType:
      dataSourceSettings && "coverages" in dataSourceSettings
        ? selectorPolicyTypesFromSettings(dataSourceSettings)
        : undefined
  });

  if (querySettings.isLoading) {
    return <WSTableCell icon="loader" text="..." secondaryText="..." />;
  }

  return <WSTableCell {...props} />;
};

export const TierCell: React.FC<{
  requirementType: RequirementType;
  requirementDataSourceSettingsId?: string;
}> = ({ requirementType, requirementDataSourceSettingsId }) => {
  const querySettings = useRequirementDefinitionsSetting({
    requirementDefinition: {
      requirementType,
      dataSourceId: requirementDataSourceSettingsId!
    },
    fetch: "contextType"
  });

  const dataSourceSettings = querySettings.data;

  const tier = getRequirementDefinitionTier({
    type: requirementType,
    contextType:
      dataSourceSettings && "contextType" in dataSourceSettings
        ? dataSourceSettings.contextType
        : undefined,
    policyType:
      dataSourceSettings && "coverages" in dataSourceSettings
        ? selectorPolicyTypesFromSettings(dataSourceSettings)
        : undefined
  });
  const tierConfig = mapRequirementDefinitionTemplateTierConfig[tier];

  if (querySettings.isLoading) {
    return <WSTableCell icon="loader" text="..." secondaryText="..." />;
  }

  return (
    <WSTableCell
      icon={tierConfig.isWayfinder ? "compass" : undefined}
      text={tierConfig.name}
      secondaryText={tierConfig.description}
    />
  );
};

export const RequirementDefinitionsTable: React.FC<RequirementsTableProps> = ({
  listPath,
  requirements,
  engagements,
  loading,
  ...elementProps
}) => {
  const requirementDefinitionDefaultActions =
    useRequirementDefinitionDefaultActions();

  const goToRequirementDefinitionDetails =
    useGoToRequirementDefinitionDetails();

  const tableData = useMemo(
    () =>
      requirements.map(requirement => ({
        id: requirement.requirementDefinitionId,
        data: requirement
      })),
    [requirements]
  );

  const columns: Array<WSTableColumn<IRequirementDefinitionResponse>> = [
    {
      config: {
        header: {
          text: "Name"
        }
      },
      renderFunction: rowItem => (
        <WSTableCell
          text={rowItem.data.name}
          secondaryText={rowItem.data.description}
        />
      )
    },
    {
      config: {
        header: {
          text: "Type"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: ({ data: requirementDefinition }) => (
        <TypeCell
          requirementType={requirementDefinition.requirementType}
          requirementDataSourceSettingsId={requirementDefinition.dataSourceId}
        />
      )
    },
    {
      config: {
        header: {
          text: "Tier"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: ({ data: requirementDefinition }) => (
        <TierCell
          requirementType={requirementDefinition.requirementType}
          requirementDataSourceSettingsId={requirementDefinition.dataSourceId}
        />
      )
    },
    {
      config: {
        header: {
          text: "Engagements"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => {
        const requirementEngagements = engagements.filter(e =>
          e.requirementDefinitionIds?.includes(rowItem.id)
        );
        const firstTwoEngagementsNames = requirementEngagements
          .slice(0, 2)
          .map(e => e.name)
          .join(", ");
        return (
          <WSTableCell
            text={firstTwoEngagementsNames}
            {...(requirementEngagements.length > 2
              ? {
                  secondaryText: `+${requirementEngagements.length - 2} more`
                }
              : {})}
          />
        );
      }
    },
    {
      config: {
        header: {
          text: "Last updated"
        },
        hideOnScreens: ["XS"]
      },
      renderFunction: rowItem => (
        <WSTableCell
          text={toWSDate(rowItem.data.updatedAt, "monthDayYear").formattedValue}
        />
      )
    },
    {
      config: {
        header: {
          text: "Status"
        }
      },
      renderFunction: ({ data: requirement }) => {
        if (requirement.status === RequirementDefinitionStatus.Active) {
          return (
            <WSTableCell
              pill={{
                icon: true,
                text: "Active",
                theme: "success"
              }}
            />
          );
        }
        return (
          <WSTableCell
            pill={{
              icon: "archive",
              text: "Archived",
              theme: "neutral"
            }}
          />
        );
      }
    }
  ];

  return (
    <WSTable<IRequirementDefinitionResponse>
      {...elementProps}
      loading={loading}
      tableData={tableData}
      rowFetchMenuActions={({ data }) => {
        return requirementDefinitionDefaultActions({
          requirementDefinition: data,
          engagements: engagements
        });
      }}
      columns={columns}
      onRowClick={({ data: requirementDefinition }) => {
        goToRequirementDefinitionDetails(
          requirementDefinition.requirementDefinitionId
        );
      }}
      onRowControlClick={({ data: requirementDefinition }) => {
        openInNewTab(
          buildRequirementDetailsPath(
            requirementDefinition.requirementDefinitionId
          )
        );
      }}
      onRowMousewheelClick={({ data: requirementDefinition }) => {
        openInNewTab(
          buildRequirementDetailsPath(
            requirementDefinition.requirementDefinitionId
          )
        );
      }}
      data-testid="requirementsTable"
    />
  );
};
